import { AudioLines } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useOptionalDictation, useVoiceQueueCount } from '@/components/voice/DictationContext';
import { isVoiceEnabled } from '@/services/voice/flag';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  /** Fecha o drawer mobile depois de abrir o modal. */
  onCloseMobile?: () => void;
  /** Versão compacta (só ícone) na sidebar desktop colapsada. */
  isDesktopCollapsed?: boolean;
}

export const PATIENT_FILE_RE = /^\/pacientes\/([^/?]+)/;

/**
 * Atalho "Ditar" na sidebar, entre o "Extrair Dados" e o "Gravar áudio"
 * (spec 009). Mesmas regras dos vizinhos: só fica ativo dentro da ficha de um
 * paciente; fora dela, aparece desabilitado.
 *
 * O badge mostra quantos documentos já estão na fila daquele paciente — é o
 * que lembra o médico de que existe coisa preparada esperando conferência,
 * mesmo depois de fechar o modal.
 */
export const SidebarDictateButton = ({ onCloseMobile, isDesktopCollapsed = false }: Props) => {
  const ctx = useOptionalDictation();
  const { pathname } = useLocation();
  const { role } = useAuth();
  const patientId = PATIENT_FILE_RE.exec(pathname)?.[1] ?? null;
  const queued = useVoiceQueueCount(patientId);

  if (!ctx) return null;

  const { available, open } = ctx;
  const onPatientFile = patientId !== null;
  const enabled = onPatientFile && available;

  /**
   * Por que está desabilitado. Sem isso o botão cinza não diz nada, e os três
   * motivos exigem ações completamente diferentes — o mais traiçoeiro é a
   * flag, que é por ORIGEM: ligar no localhost não vale no dratento.com.br.
   */
  const motivo = !isVoiceEnabled(role)
    ? 'O ditado é liberado para médico e administrador — este login não tem esse papel.'
    : !onPatientFile
      ? 'Abra um paciente para ditar documentos'
      : 'Abra a ficha do paciente (aba Entradas ou Prontuário) para o ditado ficar disponível';

  const base = `mb-2 rounded-lg flex items-center gap-3 border transition-colors font-semibold ${
    isDesktopCollapsed
      ? 'md:mx-2 md:w-[calc(100%-1rem)] md:justify-center md:px-0 md:py-2 mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
      : 'mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
  }`;

  if (!enabled) {
    return (
      <button
        type="button"
        disabled
        title={motivo}
        className={`${base} bg-surface-container/40 text-on-surface-variant/50 border-dashed border-outline-variant/40 cursor-not-allowed`}
      >
        <AudioLines size={16} className="shrink-0" />
        <span className={`text-sm flex-1 text-left ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
          Ditar
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        // Clique abre JÁ no Ao vivo, igual ao Alt+V (pedido de 02/10/2026).
        // Sem `toggle`: clicar de novo com o ao vivo ligado só reabre a coluna.
        open({ autoStart: 'live', origem: 'clique' });
        onCloseMobile?.();
      }}
      title="Ditar receita, exames e cirurgia — abre já no Ao vivo; os documentos saem prontos pra conferir. Alt+V (ou pedal) liga e desliga."
      className={`${base} relative bg-primary/15 hover:bg-primary/25 text-primary border-primary/40 ring-1 ring-primary/30 shadow-sm`}
    >
      <AudioLines size={16} className="shrink-0" />
      {/* Menu recolhido: a fila vira um selinho no canto do botão. */}
      {queued > 0 && isDesktopCollapsed && (
        <span
          className="hidden md:block absolute -top-1.5 right-1 text-[10px] font-bold rounded-full bg-primary text-on-primary px-1.5 py-0.5 leading-none"
          title={`${queued} documento(s) na fila deste paciente`}
        >
          {queued}
        </span>
      )}
      <span className={`text-sm flex-1 text-left ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
        Ditar
      </span>
      {queued > 0 && (
        <span
          className={`text-[10px] font-bold rounded-full bg-primary text-on-primary px-1.5 py-0.5 leading-none ${
            isDesktopCollapsed ? 'md:hidden' : ''
          }`}
          title={`${queued} documento(s) na fila deste paciente`}
        >
          {queued}
        </span>
      )}
    </button>
  );
};
